// LuviaRx — Admin Line Item Editor.
// Corrects an in-flight order's line items when the customer says the
// stored quantities are wrong (e.g. LX-0807-4E0CE6). Hits the admin-only
// PUT /api/admin/orders/{n}/items endpoint which recomputes subtotal,
// volume discount, coupons, personal discount, and total. NO customer
// email is sent. Only allowed on Pending / Payment Confirmed orders.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X as XIcon, Plus, Trash2, Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function EditOrderItemsModal({ token, order, onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Seed the editor with the order's current items so the admin can bump a
  // qty without retyping the whole order.
  const [items, setItems] = useState(() =>
    (order?.items || []).map((it) => ({
      product_id: it.product_id,
      variant_id: it.variant_id,
      quantity: it.quantity,
    })),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/products`)
      .then(({ data }) => { if (!cancelled) setProducts(data || []); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const productMap = useMemo(() => {
    const m = new Map();
    products.forEach((p) => m.set(p.slug, p));
    return m;
  }, [products]);

  const preview = useMemo(() => {
    let sub = 0;
    items.forEach((it) => {
      const p = productMap.get(it.product_id);
      const v = p?.variants?.find((x) => x.id === it.variant_id);
      if (!v) return;
      sub += (Number(v.price) || 0) * (Number(it.quantity) || 0);
    });
    return sub;
  }, [items, productMap]);

  const setItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () =>
    setItems((prev) => [...prev, { product_id: "", variant_id: "", quantity: 1 }]);
  const removeItem = (idx) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    setError("");
    const cleaned = items
      .filter((it) => it.product_id && it.variant_id && Number(it.quantity) > 0)
      .map((it) => ({
        product_id: it.product_id,
        variant_id: it.variant_id,
        quantity: Number(it.quantity),
      }));
    if (!cleaned.length) {
      setError("At least one line item with product + quantity is required.");
      return;
    }
    // Preserve any promo codes already on the order so the discount doesn't
    // vanish when we resync. The backend rolls back + re-records
    // redemptions internally.
    const promoCodes = (order.promo_codes || []).map((p) => p.code).filter(Boolean);
    setSubmitting(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/orders/${order.order_number}/items`,
        { items: cleaned, promo_codes: promoCodes },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onSaved?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="edit-order-items-modal"
      className="fixed inset-0 z-[60] flex items-start justify-center bg-charcoal-800/60 backdrop-blur-sm overflow-y-auto p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-cream-100 rounded-3xl shadow-2xl border border-charcoal-100 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-charcoal-100">
          <div>
            <p className="overline text-[#8B2252]">Edit order items</p>
            <h2 className="font-display text-2xl text-charcoal-800 mt-1">
              {order.order_number}
            </h2>
            <p className="text-sm text-charcoal-500 mt-1">
              Fix stale line items on a pending or confirmed order. Recomputes
              subtotal, discounts, and total. No customer email is sent.
            </p>
          </div>
          <button
            type="button"
            data-testid="edit-order-close"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-charcoal-100 text-charcoal-500 shrink-0"
            aria-label="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Warning about state gate */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 flex items-start gap-2 text-sm text-amber-900">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Only works while status is <b>Pending Payment - Zelle</b> or
              <b> Payment Confirmed</b>. Once shipped / refunded / cancelled,
              edits are blocked to keep inventory and shipping records intact.
            </span>
          </div>

          {/* Line items */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="overline">Line items</p>
              <button
                type="button"
                onClick={addItem}
                data-testid="edit-order-add-item"
                className="text-xs px-3 py-1.5 rounded-full border border-charcoal-200 hover:bg-cream-200 inline-flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Add item
              </button>
            </div>
            {loading ? (
              <p className="text-sm text-charcoal-400">Loading catalog…</p>
            ) : (
              <div className="space-y-2">
                {items.map((it, idx) => {
                  const p = productMap.get(it.product_id);
                  const variants = p?.variants || [];
                  return (
                    <div
                      key={idx}
                      data-testid={`edit-order-line-${idx}`}
                      className="grid grid-cols-12 gap-2 items-center bg-white p-3 rounded-2xl border border-charcoal-100"
                    >
                      <select
                        className="input col-span-6"
                        data-testid={`edit-order-line-${idx}-product`}
                        value={it.product_id}
                        onChange={(e) => setItem(idx, { product_id: e.target.value, variant_id: "" })}
                      >
                        <option value="">Select product</option>
                        {products.map((pp) => (
                          <option key={pp.slug} value={pp.slug}>{pp.name}</option>
                        ))}
                      </select>
                      <select
                        className="input col-span-3"
                        data-testid={`edit-order-line-${idx}-variant`}
                        value={it.variant_id}
                        onChange={(e) => setItem(idx, { variant_id: e.target.value })}
                        disabled={!it.product_id}
                      >
                        <option value="">Variant</option>
                        {variants.map((vv) => (
                          <option key={vv.id} value={vv.id}>
                            {vv.label} — ${vv.price}
                          </option>
                        ))}
                      </select>
                      <input
                        className="input col-span-2"
                        type="number" min={1} max={100}
                        data-testid={`edit-order-line-${idx}-qty`}
                        value={it.quantity}
                        onChange={(e) => setItem(idx, { quantity: e.target.value })}
                      />
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        disabled={items.length === 1}
                        data-testid={`edit-order-line-${idx}-remove`}
                        className="col-span-1 justify-self-end p-2 rounded-full hover:bg-red-50 text-red-600 disabled:opacity-30"
                        aria-label="Remove line"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Preview totals — server does the real math; this is just for feedback */}
          <section className="bg-white rounded-2xl border border-charcoal-100 p-4 text-sm">
            <div className="flex justify-between text-charcoal-500">
              <span>New subtotal (before discounts / shipping)</span>
              <span data-testid="edit-order-subtotal">{usd(preview)}</span>
            </div>
            <p className="text-[11px] text-charcoal-400 mt-2">
              Server will recompute volume, coupons, personal discount, and
              total from the promo codes already on this order.
            </p>
          </section>

          {error && (
            <div
              data-testid="edit-order-error"
              className="p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm"
            >
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-charcoal-100 bg-cream-200/40 rounded-b-3xl">
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-order-cancel"
            className="px-4 py-2 rounded-full text-sm border border-charcoal-200 hover:bg-cream-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="edit-order-submit"
            className="px-5 py-2 rounded-full text-sm font-medium bg-[#8B2252] text-white hover:bg-[#7a1e47] disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Saving…" : "Save line items"}
          </button>
        </div>
      </div>
    </div>
  );
}
